/**
 * 子パターン（#2967）のJSON入出力（#3372）。
 *
 * 運営スタッフが子パターンをJSONでコピー・取り込みできるようにする。
 * 親パターンの項目リストJSON（#2834 / #3039）と同じ流儀にそろえる。
 *
 * 項目は item_id だけで指定し、**親パターンの項目名はJSONに持たない**。
 * 項目名を焼き付けると親パターンでリネームしたときに古い値が残り、
 * 「JSONに書かれた名前」と「実際の項目名」のどちらが正か読めなくなるため。
 * 画面に出す項目名は、そのつど親パターンの item_list から引く（resolveOverrideItemNames）。
 */

import type { ItemOverrideType } from '../../types/subPattern';
import type { SubPatternOverrideCandidate } from '../../components/uiParts/modal/SubPatternEditModal';
import { pickRaw, pickString, pickBoolean } from './jsonKeyPicker';

/** JSONの上書き1件。Firestore の item_overrides と同じ形（項目名の参考情報は持たない） */
export type ItemOverrideJsonType = {
  item_id: string;
  extraction_rule?: string;
  fixed_value?: string;
  item_name?: string;
  is_ocr_target?: boolean;
};

/** JSONの子パターン1件 */
export type SubPatternJsonType = {
  sub_pattern_name: string;
  description: string;
  is_active: boolean;
  item_overrides: ItemOverrideJsonType[];
};

/**
 * JSONで受け取れるキー。
 * ここに無いキーが来たらエラーで弾く（子パターンの上書き対象を広げないため。#2967）。
 */
export const allowedSubPatternKeys: readonly string[] = [
  'sub_pattern_name',
  'subPatternName',
  'description',
  'is_active',
  'isActive',
  'item_overrides',
  'itemOverrides',
];

/** 上書き1件で受け取れるキー */
export const allowedOverrideKeys: readonly string[] = [
  'item_id',
  'itemId',
  'extraction_rule',
  'extractionRule',
  'fixed_value',
  'fixedValue',
  'item_name',
  'itemName',
  'is_ocr_target',
  'isOcrTarget',
];

/**
 * フォームの内容をJSON用のオブジェクトへ変換する。
 *
 * 上書きの中身が空のものは落とす（Firestore 保存時と同じ扱い）。
 * 候補一覧を受け取らないのは、項目名をJSONに入れないため。
 */
export const buildSubPatternJson = (input: {
  subPatternName: string;
  description: string;
  isActive: boolean;
  itemOverrides: ItemOverrideType[];
}): SubPatternJsonType => ({
  sub_pattern_name: input.subPatternName.trim(),
  description: input.description.trim(),
  is_active: input.isActive,
  item_overrides: input.itemOverrides
    .filter(override => (override.itemId ?? '') !== '')
    .map(override => ({
      item_id: override.itemId,
      // 空文字は「上書きしない」。キーごと省いて、JSONを読みやすくする
      ...((override.extractionRule ?? '').trim() !== ''
        ? { extraction_rule: override.extractionRule.trim() }
        : {}),
      ...((override.fixedValue ?? '').trim() !== ''
        ? { fixed_value: override.fixedValue.trim() }
        : {}),
      ...((override.itemName ?? '').trim() !== '' ? { item_name: override.itemName.trim() } : {}),
      ...(override.isOcrTarget !== undefined ? { is_ocr_target: override.isOcrTarget } : {}),
    }))
    .filter(
      override =>
        override.extraction_rule !== undefined ||
        override.fixed_value !== undefined ||
        override.item_name !== undefined ||
        override.is_ocr_target !== undefined,
    ),
});

/** JSON文字列として整形する（コピー・初期表示で使う） */
export const stringifySubPatternJson = (json: SubPatternJsonType | SubPatternJsonType[]): string =>
  JSON.stringify(json, null, 2);

/**
 * 上書きの配列を正規化する。
 *
 * 空文字は「上書きしない」を意味するため、キーごと省く。
 * is_ocr_target は boolean のため、指定があるときだけキーを持たせる
 * （省略と null はどちらも「上書きしない」）。
 */
const parseOverrides = (raw: Record<string, unknown>): ItemOverrideJsonType[] => {
  const overrides = pickRaw(raw, 'item_overrides', 'itemOverrides');
  if (!Array.isArray(overrides)) return [];
  return overrides
    .filter((o): o is Record<string, unknown> => typeof o === 'object' && o !== null)
    .map(o => {
      const isOcrTarget = pickBoolean(o, 'is_ocr_target', 'isOcrTarget');
      const extractionRule = pickString(o, 'extraction_rule', 'extractionRule').trim();
      const fixedValue = pickString(o, 'fixed_value', 'fixedValue').trim();
      const itemName = pickString(o, 'item_name', 'itemName').trim();
      return {
        item_id: pickString(o, 'item_id', 'itemId').trim(),
        ...(extractionRule !== '' ? { extraction_rule: extractionRule } : {}),
        ...(fixedValue !== '' ? { fixed_value: fixedValue } : {}),
        ...(itemName !== '' ? { item_name: itemName } : {}),
        ...(isOcrTarget !== undefined ? { is_ocr_target: isOcrTarget } : {}),
      };
    });
};

/** 1件ぶんのオブジェクトを正規化する。キーの綴りは snake_case を正とし camelCase も読む */
const parseOne = (raw: Record<string, unknown>): SubPatternJsonType => ({
  sub_pattern_name: pickString(raw, 'sub_pattern_name', 'subPatternName').trim(),
  description: pickString(raw, 'description', 'description').trim(),
  // 未指定は有効として扱う（Firestore 読み出し側の is_active !== false と同じ扱い）
  is_active: pickBoolean(raw, 'is_active', 'isActive') !== false,
  item_overrides: parseOverrides(raw),
});

/**
 * JSONをパースして正規化する。単体・配列のどちらも配列で返す。
 *
 * 形式が不正な場合は null を返し、呼び出し側でエラー文言を出す。
 * （検証は subPatternJsonValidation に集約するため、ここでは形だけを見る）
 */
export const parseSubPatternJson = (
  text: string,
): { items: SubPatternJsonType[]; raws: Record<string, unknown>[] } | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }

  const list = Array.isArray(parsed) ? parsed : [parsed];
  const raws = list.filter(
    (o): o is Record<string, unknown> => typeof o === 'object' && o !== null,
  );
  // 1件でもオブジェクトでないものが混ざっていたら形式エラーとして扱う
  if (raws.length !== list.length || raws.length === 0) return null;

  return { items: raws.map(parseOne), raws };
};

/**
 * 上書きの対象項目に、現在の項目名を添えて返す（画面表示用。#3372 §4.5）。
 *
 * JSONに項目名を持たない代わりに、取り込み前の確認でここから引いた名前を見せる。
 * 常に現在の item_list から引くため、親パターンでリネームしても正しい名前が出る。
 * 候補に無い項目IDは itemName を null にして、画面側で「存在しない項目」と示せるようにする。
 */
export const resolveOverrideItemNames = (
  overrides: ItemOverrideJsonType[],
  candidates: SubPatternOverrideCandidate[],
): { itemId: string; itemName: string | null }[] => {
  const nameMap = new Map(candidates.map(c => [c.itemId, c.itemName]));
  return overrides.map(override => ({
    itemId: override.item_id,
    itemName: nameMap.get(override.item_id) ?? null,
  }));
};

/** JSONの上書きをフォームが扱う camelCase へ変換する */
export const toItemOverrides = (overrides: ItemOverrideJsonType[]): ItemOverrideType[] =>
  overrides.map(override => ({
    itemId: override.item_id,
    extractionRule: override.extraction_rule ?? '',
    fixedValue: override.fixed_value ?? '',
    itemName: override.item_name ?? '',
    isOcrTarget: override.is_ocr_target,
  }));
